import { Branding, PrintHeaderType } from '../../hooks/useBranding';

interface Props {
  type:         PrintHeaderType;
  settings:     Branding;
  hospitalName: string;
}

// Maps document type → branding key that holds its custom header image
const HEADER_KEY: Record<PrintHeaderType, keyof Branding> = {
  prescription: 'print_header_prescription',
  invoice:      'print_header_invoice',
  lab:          'print_header_lab',
  radiology:    'print_header_radiology',
};

/**
 * Top-of-page header for any printed document.
 *
 * Condition A — custom image: renders a full-width <img> with no extra markup.
 * Condition B — fallback:     renders hospital logo + name + tagline + contact info.
 *
 * All layout uses inline styles so it is immune to Tailwind's PurgeCSS in @media print.
 * Wrap this component's output in a print stylesheet container that sets page margins.
 *
 * Usage:
 *   <PrintHeader type="prescription" settings={branding} hospitalName={hospital.name} />
 */
export default function PrintHeader({ type, settings, hospitalName }: Props) {
  const headerUrl = settings[HEADER_KEY[type]] as string;

  // ── Condition A: custom full-width image ─────────────────────────
  if (headerUrl) {
    return (
      <div style={{ width: '100%', marginBottom: '4mm' }}>
        <img
          src={headerUrl}
          alt={`${type} header`}
          style={{
            display:   'block',
            width:     '100%',
            maxHeight: '40mm',
            objectFit: 'contain',
            objectPosition: 'left center',
          }}
        />
      </div>
    );
  }

  // ── Condition B: text fallback — logo + identity + contact ───────
  const initial = hospitalName.trim().slice(0, 1).toUpperCase();

  return (
    <div
      style={{
        display:       'flex',
        alignItems:    'flex-start',
        gap:           '10px',
        paddingBottom: '6px',
        borderBottom:  '2px solid #1a1a2e',
        marginBottom:  '4mm',
        width:         '100%',
      }}
    >
      {/* Logo or monogram */}
      <div style={{ flexShrink: 0 }}>
        {settings.branding_logo ? (
          <img
            src={settings.branding_logo}
            alt="logo"
            style={{ width: '14mm', height: '14mm', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <div
            style={{
              width:           '14mm',
              height:          '14mm',
              background:      '#1a1a2e',
              borderRadius:    '3mm',
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'center',
              color:           '#ffffff',
              fontSize:        '10pt',
              fontWeight:      700,
              flexShrink:      0,
            }}
          >
            {initial}
          </div>
        )}
      </div>

      {/* Hospital identity */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '13pt', lineHeight: 1.2, color: '#111' }}>
          {hospitalName}
        </div>

        {settings.branding_tagline && (
          <div style={{ fontSize: '7.5pt', color: '#555', marginTop: '1px' }}>
            {settings.branding_tagline}
          </div>
        )}

        {/* Contact line */}
        <div
          style={{
            display:    'flex',
            flexWrap:   'wrap',
            gap:        '10px',
            marginTop:  '3px',
            fontSize:   '7.5pt',
            color:      '#444',
          }}
        >
          {settings.hospital_address && (
            <span>{settings.hospital_address}</span>
          )}
          {settings.hospital_phone && (
            <span>&#9990;&nbsp;{settings.hospital_phone}</span>
          )}
          {settings.hospital_email && (
            <span>&#9993;&nbsp;{settings.hospital_email}</span>
          )}
          {settings.branding_license_no && (
            <span>Lic:&nbsp;{settings.branding_license_no}</span>
          )}
        </div>
      </div>
    </div>
  );
}
